// Synthesised softphone tones. Web Audio is used directly so we don't have to
// bundle/serve audio assets. Three tones cover the call lifecycle:
//
//   IncomingRingtone — ascending two-note chime while an INVITE is ringing.
//   OutboundRingback — UK PSTN-style ringback (400+450Hz, 0.4s/0.2s/0.4s/2s)
//                      while we wait for the dialled party to answer.
//   EndCallTone      — short descending beep when a call disconnects, so the
//                      agent has audible confirmation the line has closed.

type AudioCtxCtor = typeof AudioContext;

function getAudioCtxCtor(): AudioCtxCtor | null {
  if (typeof window === 'undefined') return null;
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext ?? null;
}

function createCtx(): AudioContext | null {
  const Ctx = getAudioCtxCtor();
  if (!Ctx) return null;
  try {
    return new Ctx();
  } catch {
    return null;
  }
}

export class IncomingRingtone {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.ctx) return;
    this.ctx = createCtx();
    if (!this.ctx) return;
    this.play();
    this.timer = setInterval(() => this.play(), 2400);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    void this.ctx?.close().catch(() => null);
    this.ctx = null;
  }

  private play(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    tone(this.ctx, t + 0.0, 880, 0.18, 0.22);
    tone(this.ctx, t + 0.2, 1175, 0.22, 0.22);
    tone(this.ctx, t + 0.55, 880, 0.18, 0.22);
    tone(this.ctx, t + 0.75, 1175, 0.22, 0.22);
  }
}

// UK ringback cadence: two 400ms bursts 200ms apart, then 2s of silence —
// the familiar "brrr-brrr" you hear when you call a UK landline.
export class OutboundRingback {
  private ctx: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.ctx) return;
    this.ctx = createCtx();
    if (!this.ctx) return;
    this.play();
    this.timer = setInterval(() => this.play(), 3000);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    void this.ctx?.close().catch(() => null);
    this.ctx = null;
  }

  private play(): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    // 400Hz + 450Hz dual-frequency burst, played twice per cycle.
    tone(this.ctx, t + 0.0, 400, 0.4, 0.16);
    tone(this.ctx, t + 0.0, 450, 0.4, 0.16);
    tone(this.ctx, t + 0.6, 400, 0.4, 0.16);
    tone(this.ctx, t + 0.6, 450, 0.4, 0.16);
  }
}

// One-shot descending beep on hangup so the agent gets audible confirmation
// that the line has closed (mirrors the "phone hung up" cue on iOS / Android).
export function playEndCallTone(): void {
  const ctx = createCtx();
  if (!ctx) return;
  const t = ctx.currentTime;
  tone(ctx, t + 0.0, 620, 0.14, 0.22);
  tone(ctx, t + 0.14, 440, 0.22, 0.22);
  // Close the context once the last tone has finished decaying so we don't
  // leak AudioContexts on every call.
  setTimeout(() => void ctx.close().catch(() => null), 600);
}

function tone(ctx: AudioContext, when: number, freq: number, duration: number, peak: number): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(peak, when + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, when + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(when);
  osc.stop(when + duration + 0.05);
}
